import { ApplicationConfig, APP_INITIALIZER, PLATFORM_ID, ErrorHandler } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideClientHydration } from '@angular/platform-browser';
import {
  KeycloakService,
  includeBearerTokenInterceptor,
  createInterceptorCondition,
  IncludeBearerTokenCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG
} from 'keycloak-angular';

import { routes } from './app.routes';
import { initializeKeycloak } from './app.init';
import { environment } from '../environments/environment';

/**
 * Configuration des URLs qui doivent recevoir le token Bearer
 * 
 * IMPORTANT : Cette condition utilise maintenant la configuration d'environnement
 * et accepte TOUTES les URLs d'API (pas seulement auth.cineri.sn/api)
 * 
 * Exclusions :
 * - /assets/* (fichiers statiques)
 * - /api/public/* (endpoints publics)
 */
const createBearerTokenCondition = () => {
  // Extraire le domaine de base de l'URL d'API
  const apiBaseUrl = environment.api.baseUrl.replace(/^https?:\/\//, '');
  const authBaseUrl = environment.keycloak.url.replace(/^https?:\/\//, '');
  
  return createInterceptorCondition<IncludeBearerTokenCondition>({
    // Pattern qui accepte toutes les URLs de vos APIs
    urlPattern: new RegExp(
      `^https?:\\/\\/(${apiBaseUrl}|${authBaseUrl})(\\/.*)?$`,
      'i'
    ),
  });
};

/**
 * Global Error Handler pour capturer les erreurs non gérées
 */
class GlobalErrorHandler implements ErrorHandler {
  handleError(error: any): void {
    console.error('Erreur globale capturée:', error);
    
    // En production, envoyer à un service de monitoring
    if (environment.production && environment.features.enableErrorTracking) {
      // TODO: Intégrer avec Sentry, LogRocket, etc.
      // this.errorTrackingService.logError(error);
    }
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    // HTTP Client avec intercepteur pour le token
    provideHttpClient(
      withInterceptors([includeBearerTokenInterceptor])
    ),
    
    // Router
    provideRouter(routes),
    
    // Client Hydration pour SSR
    provideClientHydration(),
    
    // Date Adapter pour Material
    provideNativeDateAdapter(),
    
    // Keycloak Service
    KeycloakService,
    
    // Configuration de l'intercepteur Bearer Token (dynamique selon l'environnement)
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [createBearerTokenCondition()]
    },
    
    // Global Error Handler
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    
    // Initialisation de Keycloak au démarrage de l'application (côté client uniquement)
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService, PLATFORM_ID]
    }
  ]
};